import {
    type GoalCategory,
    GoalStatus,
    type WorkflowGoal,
} from '@fruitful/types';
import { sortBy } from 'lodash';
import { Column } from 'primereact/column';
import {
    DataTable,
    type DataTableRowEditCompleteEvent,
    type DataTableRowEvent,
    type DataTableRowReorderEvent,
} from 'primereact/datatable';
import React, { useState } from 'react';
import { IconButton, TrashIcon, YStack } from 'smoothie';

import { TextEditor } from '../../common/components/GridCellEditors';
import { AddNewWorkflowGoal } from './AddNewWorkflowGoal';
import { GoalCategoryDropdownEditor } from './GoalCategoryDropdown';
import {
    type WorkflowGoalTaskEphemeralId,
    WorkflowGoalTasksSubtable,
} from './WorkflowGoalTasksSubtable';

export interface WorkflowGoalEphemeralId extends WorkflowGoal {
    id: string;
    tasks: WorkflowGoalTaskEphemeralId[];
}

interface WorkflowGoalsTableProps {
    membershipId: string;
    goals: WorkflowGoalEphemeralId[];
    onGoalsChange: (updatedGoals: WorkflowGoalEphemeralId[]) => void;
    disabled?: boolean;
}

export const WorkflowGoalsTable = ({
    membershipId,
    goals,
    onGoalsChange,
    disabled = false,
}: WorkflowGoalsTableProps) => {
    // Add an ephemeralId onto local goals, so Datatable can track which are expanded/collapsed
    const [updatedGoals, setUpdatedGoals] = useState(
        goals.map((goal) => ({
            ...goal,
            id: goal.description,
            tasks: (goal.tasks ?? []).map((task, index) => ({
                ...task,
                position: index,
                id: task.description,
            })),
        })),
    );
    const [expandedGoals, setExpandedGoals] = useState<Record<string, boolean>>(
        {},
    );

    const updateGoals = (updatedGoal: WorkflowGoalEphemeralId) => {
        const nextGoals = sortBy(
            [
                ...updatedGoals.filter((goal) => goal.id !== updatedGoal.id),
                updatedGoal,
            ],
            'position',
        );

        setUpdatedGoals(nextGoals);
        onGoalsChange(nextGoals);
    };

    const deleteGoal = (goal: WorkflowGoal) => {
        const nextGoals = updatedGoals
            .filter((g) => g.description !== goal.description)
            .map((g, index) => ({ ...g, position: index }));

        setUpdatedGoals(nextGoals);
        onGoalsChange(nextGoals);
    };

    const onRowReorder = ({
        value: reorderedGoals,
    }: DataTableRowReorderEvent<WorkflowGoalEphemeralId[]>) => {
        const nextGoals = reorderedGoals.map((goal, index) => ({
            ...goal,
            position: index,
        }));

        setUpdatedGoals(nextGoals);
        onGoalsChange(nextGoals);
    };

    const onRowEditComplete = ({ newData }: DataTableRowEditCompleteEvent) => {
        updateGoals(newData as WorkflowGoalEphemeralId);
    };

    const rowExpansionTemplate = (goal: WorkflowGoalEphemeralId) => {
        return (
            <WorkflowGoalTasksSubtable
                tasks={goal.tasks ?? []}
                onUpdateTasks={(
                    updatedTasks: WorkflowGoalTaskEphemeralId[],
                ) => {
                    const updatedGoal = { ...goal, tasks: updatedTasks };
                    updateGoals(updatedGoal);
                }}
                disabled={disabled}
            />
        );
    };

    const onRowExpand = ({ data }: DataTableRowEvent) => {
        setExpandedGoals({
            ...expandedGoals,
            [(data as WorkflowGoalEphemeralId).id]: true,
        });
    };

    const onRowCollapse = ({ data }: DataTableRowEvent) => {
        const goalId = (data as WorkflowGoalEphemeralId).id;
        const { [goalId]: _, ...rest } = expandedGoals;
        setExpandedGoals(rest);
    };

    return (
        <YStack>
            <DataTable
                showHeaders={false}
                dataKey="id"
                value={updatedGoals}
                size="small"
                reorderableRows={!disabled}
                onRowReorder={disabled ? undefined : onRowReorder}
                editMode={disabled ? undefined : 'row'}
                onRowEditComplete={disabled ? undefined : onRowEditComplete}
                expandedRows={expandedGoals}
                rowExpansionTemplate={rowExpansionTemplate}
                onRowExpand={onRowExpand}
                onRowCollapse={onRowCollapse}
            >
                <Column expander />
                <Column field="position" sortable hidden />
                <Column
                    field="category"
                    header="Category"
                    style={{ width: '12rem', fontSize: '0.875rem' }}
                    editor={
                        disabled
                            ? undefined
                            : (options) => (
                                  <GoalCategoryDropdownEditor
                                      options={options}
                                  />
                              )
                    }
                />
                <Column
                    field="description"
                    header="Goal"
                    style={{ fontSize: '0.875rem' }}
                    editor={
                        disabled
                            ? undefined
                            : (options) => <TextEditor options={options} />
                    }
                />
                {!disabled && <Column rowEditor style={{ width: '2rem' }} />}
                {!disabled && (
                    <Column
                        style={{ width: '2rem' }}
                        body={(goal: WorkflowGoal) => (
                            <YStack justifyContent="center" alignItems="center">
                                <IconButton
                                    variant="tertiary"
                                    tone="danger"
                                    icon={TrashIcon}
                                    onPress={() => {
                                        deleteGoal(goal);
                                    }}
                                />
                            </YStack>
                        )}
                    />
                )}
                {!disabled && <Column rowReorder style={{ width: '2rem' }} />}
            </DataTable>
            {!disabled && (
                <AddNewWorkflowGoal
                    onAdd={(goal: {
                        category: GoalCategory;
                        description: string;
                    }) => {
                        updateGoals({
                            id: goal.description ?? '',
                            position: updatedGoals.length,
                            status: GoalStatus.IN_PROGRESS,
                            membershipId,
                            tasks: [],
                            ...goal,
                        });
                    }}
                />
            )}
        </YStack>
    );
};
